import Image from 'next/legacy/image'
import styled, { css } from 'styled-components'
import noop from 'lodash/noop'

import structure from '@/structure'
import { colors } from '@/styles'
import CdnImage from '@/components/cdn-image'

import camera from './camera.png'

const MIN_GRID = 1
const MAX_GRID = 5

const ImageGrid = styled.button`
  user-select: none;
  position: relative;
  height: 100%;
  width: 100%;
  cursor: ${props => (props.$pointer ? 'pointer' : 'auto')};
  ${props =>
    props.$count === 1 &&
    `
    > span > img {
      background-repeat: no-repeat;
    }
  `};
`

const singleImage = css`
  > ${ImageGrid}:first-of-type {
    background: ${colors.background};
    object-fit: contain;
    box-shadow: #f0f0f0 0px 0px 0px 1px;
  }
  clip-path: inset(-2px -2px -15px -2px round 10px);
  border: 1px solid ${colors.grey88};
`

const topSpan = css`
  > ${ImageGrid}:nth-of-type(2) {
    grid-column: 2 span;
  }
`

const bottomSpan = css`
  > ${ImageGrid}:last-of-type {
    grid-column: 2 span;
  }
`

const rightSpan = css`
  > ${ImageGrid}:last-of-type {
    grid-row: 2 span;
    grid-column: 2 span;
  }
`

const MaxWidth = styled.div`
  margin: 0 auto;
  display: flex;
  max-width: ${structure.page.width + 40}px;
  justify-content: center;
  @media only screen and (max-width: 1025px) {
    max-width: 820px;
  }
`

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  border-radius: 10px;
  display: grid;
  margin: 0 20px;
  grid-gap: 12px;
  grid-template-columns: repeat(4, 0.25fr);
  grid-template-rows: repeat(2, 1fr);
  height: 500px;
  overflow: hidden;

  ${props =>
    props.blurred &&
    `
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 51;
    border-radius: 10px;
  }
  `}

  @media only screen and (max-width: 1025px) {
    grid-template-rows: repeat(2, 217.5px);
    height: 435px;
  }

  @media only screen and (max-width: 599px) {
    grid-template-rows: repeat(2, 180px);
    height: 360px;
  }

  > ${ImageGrid}:first-of-type {
    grid-row: 2 span;
    grid-column: ${props => (props.$count > MIN_GRID ? 2 : 4)} span;
  }

  ${props => props.$count === 2 && rightSpan};
  ${props => props.$count === 3 && bottomSpan};
  ${props => (props.$count === 3 || props.$count === 4) && topSpan};

  ${props => props.$count === 1 && singleImage};
`

const Count = styled.div`
  pointer-events: none;
  position: absolute;
  top: 17px;
  left: 17px;
  height: 33px;
  color: ${colors.mediumLightGrey};
  background: rgba(0, 0, 0, 0.7);
  border-radius: 7px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
  z-index: 50;
`

const CameraWrapper = styled.div`
  position: relative;
  width: 25px;
  height: 25px;
  margin-right: 5px;
`

const Text = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  margin-left: 5px;
`

const fullWidth = '100vw'
const halfWidth = '50vw'
const quaterWidth = '25vw'

// Gets the needed image size depending
// on how much the image spans
const getSize = (index, count) => {
  // Full span
  if (count === 1) return fullWidth
  // Right span
  if (count === 2 && index === 2) return halfWidth
  // Bottom span
  if (count === 3 && index === 3) return halfWidth
  // Top span
  if ((count === 3 || count === 4) && index === 2) return halfWidth
  // Left span
  if (index === 1) return halfWidth
  // No span
  return quaterWidth
}

const PhotoGrid = ({
  images,
  openImage,
  onMouseDown = noop,
  blurred = false
}) => {
  const count = images?.length || 0
  const display = images.slice(0, Math.min(count, MAX_GRID))
  const onImageOpen = openImage ?? noop

  return (
    <MaxWidth>
      <Wrapper $count={count} blurred={blurred}>
        <Count>
          <CameraWrapper>
            <Image src={camera} width={26} height={26} alt="" />
          </CameraWrapper>
          <Text>{count}</Text>
        </Count>
        {display.map((image, index) => (
          <ImageGrid
            tabIndex="-1"
            key={
              blurred ? `blurred-photo-grid-${index}` : `photo-grid-${index}`
            }
            onClick={blurred ? null : onImageOpen(index)}
            onMouseDown={blurred ? null : onMouseDown}
            $pointer={blurred ? null : openImage}
            $count={count}
          >
            <CdnImage
              src={image}
              sizes={`${getSize(index + 1, count)}`}
              layout="fill"
              placeholder="blur"
              objectPosition="center"
              objectFit={count !== 1 ? 'cover' : 'contain'}
              alt=""
              priority={index === 0}
            />
          </ImageGrid>
        ))}
      </Wrapper>
    </MaxWidth>
  )
}

export default PhotoGrid
